import { createSelector } from "@reduxjs/toolkit";
import MiniSearch from "minisearch";
import {
  selectActiveSearch,
  selectClickedMcBimItemId,
  selectComponentSearchText,
  selectRoomSearchText,
} from "./viewerSlice";
import { selectMcBimItemsForViewer } from "../McBim/mcbimItemSelectors";
import { openapi, type PostMcbimItemsApiResponse } from "../../openapi";
import { selectHttpMcBimSpaceItems } from "../McBim/httpMcBimSelectors";
import type {
  MultiViewerStreetViewSpatialItem,
  MultiViewerStreetViewItem3d,
  RoomSectionRenderItem,
} from "@multiconsult-group/multiplacer-ui";
import { getMcBimItemDisplayColor } from "../DesignTab/transformMCBimItemsToPolygons";
import { IfcClassesEnum } from "@multiconsult-group/multiplacer-core";
import type { IRootState } from "../app/store";
import { selectProjectNoAndFloorName } from "../McBim/McBimSlice";
import { selectDuplicateSpaces } from "../McBim/spaceDuplicateSelectors";

/* -------------------------------------------------------------------------- */
/* Utils                                                                      */
/* -------------------------------------------------------------------------- */

const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

const looksLikeRoomNo = (q: string) => /^[0-9.]+$/.test(q);

const EMPTY_MCBIM_ITEMS: PostMcbimItemsApiResponse = [];

const toStreetViewSpatialItem = (
  item: PostMcbimItemsApiResponse[number]
): MultiViewerStreetViewSpatialItem => ({
  id: String(item._id),
  centerPoint: item.centerPoint,
  bbox: item.bbox,
  polygon: item.polygon,
});

const makeSelectStreetViewContextItemsByClass = (instanceClass: string) =>
  createSelector(
    [selectProjectNoAndFloorName, (state: IRootState) => state],
    (projectAndFloor, state): PostMcbimItemsApiResponse => {
      if (!projectAndFloor) return EMPTY_MCBIM_ITEMS;

      return (
        openapi.endpoints.postMcbimItems.select({
          body: {
            projectNo: projectAndFloor.projectNo,
            buildingStoryName: projectAndFloor.buildingStoryName,
            instanceClasses: [instanceClass],
          },
        })(state).data ?? EMPTY_MCBIM_ITEMS
      );
    }
  );

const selectStreetViewWallItems = makeSelectStreetViewContextItemsByClass(IfcClassesEnum.IFCWALL);
const selectStreetViewWallStandardCaseItems = makeSelectStreetViewContextItemsByClass(
  IfcClassesEnum.IFCWALLSTANDARDCASE
);
const selectStreetViewSlabItems = makeSelectStreetViewContextItemsByClass("IfcSlab");

const selectStreetViewContextItems = createSelector(
  [
    selectStreetViewWallItems,
    selectStreetViewWallStandardCaseItems,
    selectStreetViewSlabItems,
  ],
  (wallItems, wallStandardCaseItems, slabItems): PostMcbimItemsApiResponse => {
    const itemsById = new Map<string, PostMcbimItemsApiResponse[number]>();
    for (const items of [wallItems, wallStandardCaseItems, slabItems]) {
      for (const item of items) {
        itemsById.set(String(item._id), item);
      }
    }

    return Array.from(itemsById.values());
  }
);

export const selectStreetViewItemsById = createSelector(
  [selectMcBimItemsForViewer, selectStreetViewContextItems],
  (items, contextItems) => {
    const itemsById = new Map<string, PostMcbimItemsApiResponse[number]>();

    for (const item of items) {
      itemsById.set(String(item._id), item);
    }
    for (const item of contextItems) {
      itemsById.set(String(item._id), item);
    }

    return itemsById;
  }
);

export const selectStreetViewItemRenderer = createSelector(
  [
    selectStreetViewItemsById,
    (state: IRootState) => state.design.mcbimItemColorMode,
    selectClickedMcBimItemId,
  ],
  (itemsById, mcbimItemColorMode, clickedItemId) =>
    (item: MultiViewerStreetViewItem3d): RoomSectionRenderItem => {
      // Cached room items include the resolved project-specific color metadata.
      const colorItem = itemsById.get(String(item._id)) ?? item;
      const baseColor =
        getMcBimItemDisplayColor({ mcbimItemColorMode, item: colorItem }) ??
        getMcBimItemDisplayColor({ mcbimItemColorMode: "MULTIPLACER_COLORS", item }) ??
        "#000000";
      const isSelected = clickedItemId != null && String(item._id) === String(clickedItemId);

      return {
        item,
        color: isSelected ? "#dc4242" : baseColor,
        opacity: 1,
        outline: isSelected
          ? { color: "#dc4242", opacity: 1, width: 4, renderOnTop: true }
          : { color: "#000000", opacity: 1, width: 2 },
      };
    }
);

export const selectStreetViewSpatialItems = createSelector(
  [selectStreetViewItemsById],
  (itemsById): MultiViewerStreetViewSpatialItem[] =>
    Array.from(itemsById.values(), toStreetViewSpatialItem)
);

/**
 * Smart tokenizer:
 *  - splitter på whitespace + skilletegn
 *  - legger til versjon uten . _ -  (sta.001 → sta001)
 */
const tokenize = (text: string): string[] => {
  const s = norm(text);

  const raw = s.split(/[\s/(),;:+]+/g).filter(Boolean);

  const normalized = raw.map((t) => t.replace(/[._-]/g, "")).filter(Boolean);

  return Array.from(new Set([...raw, ...normalized]));
};

/* -------------------------------------------------------------------------- */
/* Space free-text search                                                     */
/* -------------------------------------------------------------------------- */

export const selectMcbimSpaceHitSetByFreeTextSearch = createSelector(
  [selectHttpMcBimSpaceItems, selectRoomSearchText, selectDuplicateSpaces],
  (spaces, freeText, duplicates): Set<string> | null => {
    // Keep the invalid source data visible and let the warning explain why search is paused.
    if (duplicates.length > 0) return null;
    const query = freeText?.trim();
    if (!query) return null;

    if (looksLikeRoomNo(query)) {
      return new Set(spaces.filter((s) => (s.name ?? "").startsWith(query)).map((s) => s.name));
    }

    const mini = new MiniSearch<(typeof spaces)[number]>({
      fields: ["name", "longName"],
      storeFields: ["name", "longName"],
      idField: "name",
      tokenize,
      searchOptions: {
        prefix: true,
        combineWith: "OR",
      },
    });

    mini.addAll(spaces);
    const results = mini.search(query);

    return new Set(results.map((r) => r.id as string));
  }
);

/* -------------------------------------------------------------------------- */
/* McBim item free-text search                                                */
/* -------------------------------------------------------------------------- */

// Canonicalize codes instead of emitting aliases as separate required search terms.
// In particular, the separator in autocomplete labels ("code - name") is not a term.
const tokenizeComponentSearch = (text: string): string[] =>
  Array.from(new Set(norm(text)
    .split(/[\s/(),;:+]+/g)
    .map(term => term.replace(/[._-]/g, ""))
    .filter(term => /[\p{L}\p{N}]/u.test(term))));

/**
 * Index byggs endast när items-array ändras.
 */
export const selectMcbimItemsMiniSearchIndex = createSelector(
  [selectMcBimItemsForViewer],
  (items) => {
    const mini = new MiniSearch<(typeof items)[number]>({
      fields: ["name", "compCode", "instanceClass", "system", "typeName"],
      storeFields: ["name", "compCode", "instanceClass", "system", "typeName"],
      idField: "_id",
      tokenize: tokenizeComponentSearch,
      searchOptions: {
        prefix: true,
        combineWith: "AND",
      },
      extractField: (doc, fieldName) => {
        const v = doc[fieldName as keyof typeof doc];
        return typeof v === "string" ? v : "";
      },
    });

    mini.addAll(items);
    return mini;
  }
);

/**
 * Sök körs endast när query ändras.
 */
export const selectMcbimItemHitSetByFreeTextSearch = createSelector(
  [selectMcbimItemsMiniSearchIndex, selectComponentSearchText],
  (mini, freeText): Set<string> | null => {
    const query = freeText?.trim();
    if (!query) return null;

    const results = mini.search(query);
    return new Set(results.map((r) => r.id as string));
  }
);

/* -------------------------------------------------------------------------- */
/* CenterPoint map                                                            */
/* -------------------------------------------------------------------------- */

type IPoint = { x: number; y: number };

const toPoint = (arr: number[]): IPoint => ({
  x: arr[0]!,
  y: arr[1]!,
});

/**
 * _id -> centerPoint
 */
export const selectMcbimItemCenterByGlobalId = createSelector(
  [selectMcBimItemsForViewer],
  (items) => {
    const map = new Map<string, IPoint>();

    for (const it of items as PostMcbimItemsApiResponse) {
      map.set(it._id, toPoint(it.centerPoint!));
    }

    return map;
  }
);

/* -------------------------------------------------------------------------- */
/* Geometry utils                                                             */
/* -------------------------------------------------------------------------- */

const toPointArray = (pts: number[][]): IPoint[] => pts.map((p) => ({ x: p[0]!, y: p[1]! }));

const pointInPolygon = (p: IPoint, poly: IPoint[]): boolean => {
  let inside = false;

  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;

    const intersect = yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
};

/* -------------------------------------------------------------------------- */
/* Spaces containing hits                                                     */
/* -------------------------------------------------------------------------- */

export interface ISpaceWithHitItems {
  spaceName: string;
  spaceLongName?: string;
  spaceGlobalId: string;
  hitItemGlobalIds: string[];
}

export const selectSpacesWithMcbimItemsHitsByFreeTextSearch = createSelector(
  [
    selectHttpMcBimSpaceItems,
    selectMcbimItemHitSetByFreeTextSearch,
    selectMcbimItemCenterByGlobalId,
  ],
  (spaces, hitSet, centerById): ISpaceWithHitItems[] | null => {
    if (hitSet == null) return null;
    if (hitSet.size === 0) return [];

    const hitCenters: Array<{ id: string; c: IPoint }> = [];

    for (const id of hitSet) {
      const c = centerById.get(id);
      if (c) hitCenters.push({ id, c });
    }

    if (!hitCenters.length) return [];

    const out: ISpaceWithHitItems[] = [];

    for (const s of spaces) {
      const poly = toPointArray(s.polygon!);

      const hitsInRoom: string[] = [];

      for (const hc of hitCenters) {
        if (pointInPolygon(hc.c, poly)) {
          hitsInRoom.push(hc.id);
        }
      }

      if (hitsInRoom.length) {
        out.push({
          spaceName: s.name,
          spaceLongName: s.longName,
          spaceGlobalId: s.globalId ?? s.name,
          hitItemGlobalIds: hitsInRoom,
        });
      }
    }

    out.sort((a, b) => b.hitItemGlobalIds.length - a.hitItemGlobalIds.length);

    return out;
  }
);

export const selectSpaceNumbersWithMcbimItemHitsByFreeTextSearch = createSelector(
  [selectSpacesWithMcbimItemsHitsByFreeTextSearch],
  (spacesWithHits): string[] => {
    if (!spacesWithHits?.length) return [];
    return spacesWithHits.map((s) => s.spaceName);
  }
);

/* -------------------------------------------------------------------------- */
/* Warning selector                                                           */
/* -------------------------------------------------------------------------- */

export const selectShouldWarnNoComponentsVisibleForComponentSearch = createSelector(
  [selectActiveSearch, selectComponentSearchText, selectMcBimItemsForViewer],
  (activeSearch, componentQuery, items): boolean => {
    if (activeSearch !== "COMPONENT") return false;

    const q = componentQuery?.trim();
    if (!q) return false;

    return (items?.length ?? 0) === 0;
  }
);

export interface IComponentAutocompleteItem {
  compCode: string;
  name: string;
  value: string; // "compCode - name"
}

export interface IComponentAutocompleteUniques {
  compCodes: string[];
  names: string[];
  typeNames: string[];
  systems: string[];
}

const clean = (s: unknown): string => (typeof s === "string" ? s.trim() : "");

export const selectComponentAutocompleteValues = createSelector(
  [selectMcBimItemsForViewer],
  (items): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];

    const add = (val: string) => {
      if (!val || seen.has(val)) return;
      seen.add(val);
      out.push(val);
    };

    for (const it of items) {
      const compCode = clean(it.compCode);
      const name = clean(it.name);
      const typeName = clean(it.typeName);
      const system = clean(it.system);

      // Primær: compCode + name
      if (compCode) {
        if (name && name !== compCode) {
          add(`${compCode} - ${name}`);
        } else if (typeName) {
          add(`${compCode} - ${typeName}`);
        } else {
          add(compCode);
        }
      }

      // Sekundære
      add(name);
      add(typeName);
      add(system);
    }

    return out.sort((a, b) => a.localeCompare(b, "nb"));
  }
);
